import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import type { ContaPagarResumoDto, ContaReceberResumoDto } from "../types";

interface MonthlyComparisonProps {
  contasPagar: ContaPagarResumoDto[];
  contasReceber: ContaReceberResumoDto[];
}

export const MonthlyComparison = ({
  contasPagar,
  contasReceber,
}: MonthlyComparisonProps) => {
  const comparisonData = useMemo(() => {
    const months = [];
    const now = new Date();

    // Get last 6 months
    for (let i = 5; i >= 0; i--) {
      const monthDate = subMonths(now, i);
      const monthStart = format(startOfMonth(monthDate), "yyyy-MM-dd");
      const monthEnd = format(endOfMonth(monthDate), "yyyy-MM-dd");
      const monthLabel = format(monthDate, "MMM/yy");

      // Calculate received amounts for the month
      const recebimentos = contasReceber
        .filter(c => {
          if (!c.isPago || !c.dtPagamento) return false;
          const data = c.dtPagamento.slice(0, 10);
          return data >= monthStart && data <= monthEnd;
        })
        .reduce((sum, c) => sum + c.valorPago, 0);

      // Calculate paid amounts for the month
      const pagamentos = contasPagar
        .filter(c => {
          if (!c.isPago || !c.dtPagamento) return false;
          const data = c.dtPagamento.slice(0, 10);
          return data >= monthStart && data <= monthEnd;
        })
        .reduce((sum, c) => sum + (c.valorPago ?? c.valorTotal), 0);

      months.push({
        mes: monthLabel,
        entradas: recebimentos,
        saidas: pagamentos,
        saldo: recebimentos - pagamentos,
      });
    }

    // Calculate variations
    return months.map((month, index) => {
      if (index === 0) {
        return { ...month, variacaoEntradas: 0, variacaoSaidas: 0 };
      }

      const prevMonth = months[index - 1];
      const variacaoEntradas = prevMonth.entradas !== 0
        ? ((month.entradas - prevMonth.entradas) / prevMonth.entradas) * 100
        : 0;
      const variacaoSaidas = prevMonth.saidas !== 0
        ? ((month.saidas - prevMonth.saidas) / prevMonth.saidas) * 100
        : 0;

      return {
        ...month,
        variacaoEntradas,
        variacaoSaidas,
      };
    });
  }, [contasPagar, contasReceber]);

  const currentMonth = comparisonData[comparisonData.length - 1];
  const previousMonth = comparisonData[comparisonData.length - 2];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comparação Mensal</CardTitle>
        <CardDescription>
          Evolução de entradas e saídas nos últimos 6 meses
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={comparisonData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="mes"
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
              tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number) => [
                value.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
              ]}
            />
            <Legend />
            <Bar
              dataKey="entradas"
              fill="hsl(var(--chart-1))"
              name="Entradas"
              radius={[8, 8, 0, 0]}
            />
            <Bar
              dataKey="saidas"
              fill="hsl(var(--chart-2))"
              name="Saídas"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
        
        {currentMonth && previousMonth && (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">Variação de Entradas</h4>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${currentMonth.variacaoEntradas >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {currentMonth.variacaoEntradas >= 0 ? '+' : ''}{currentMonth.variacaoEntradas.toFixed(1)}%
                </span>
                <span className="text-sm text-muted-foreground">vs mês anterior</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">Variação de Saídas</h4>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${currentMonth.variacaoSaidas <= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {currentMonth.variacaoSaidas >= 0 ? '+' : ''}{currentMonth.variacaoSaidas.toFixed(1)}%
                </span>
                <span className="text-sm text-muted-foreground">vs mês anterior</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
